import { AuthConsumerRenderProps } from 'auth/AuthContext';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import _ from 'lodash';
import React from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { PaymentMethod } from 'ts-types/api.enums';
import { EmployeeDefaultParameters, InterventionTestBookingSearchResult, ProductDto } from 'ts-types/api.types';

const ResultsPane = styled.div`
  flex: 0 0 auto;
  margin-right: 15px;
  min-width: 15rem;
  max-width: 36rem;

  .results-header {
    font-weight: bold;
    font-size: 1.1rem;
    padding-bottom: .5rem;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 0.5rem;
  }

  .results-container {
    display: grid;
    grid-template-columns:
        minmax(min-content, 1fr)
        minmax(7rem, min-content)
        minmax(min-content, 1fr)
        minmax(7rem, min-content);
    row-gap: 0.35rem;
    column-gap: 1.2rem;
    font-size: 1rem;

    .intervention-info-property {
      color: #575757;
      grid-column: 1;
    }

    .intervention-info-property.second-column {
      grid-column: 3;
    }

    .intervention-info-value {
      font-weight: bold;
    }

    .intervention-info-value.single-value {
      grid-column-start: 2;
      grid-column-end: 5;
    }
  }

`;

const StyledIcon = styled(Icon)`
  color: rgba(0, 0, 0, .87);
`;

export interface InterventionInfoViewProps extends MessageSourceProps,
    AuthConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps {

  interventionString: string,
  interventionInfo: InterventionTestBookingSearchResult,
  invoiceProduct?: ProductDto,
  employeeDefaultParameters?: EmployeeDefaultParameters,
  interventionPricePatient: number,
  interventionPriceInsurance: number
}

const InterventionInfoView = (props: InterventionInfoViewProps) => {

  const {
    interventionInfo,
    invoiceProduct,
    employeeDefaultParameters,
    interventions,
    interventionPriceInsurance,
    interventionPricePatient,
    productMap,
    currentUser,
    getMessage
  } = props;

  const intervention = interventionInfo.interventionId
      ? interventions.find(m => m.id === interventionInfo.interventionId)
      : undefined;
  const externalCode = intervention ? intervention.externalCode : "";

  let productText = "";
  const productId = interventionInfo.productId;
  if (productId) {
    const product = productMap[productId];
    if (product) {
      const defaultDesc = !_.isEmpty(product.description) ? product.description : undefined;

      const texts: { [key: string]: string | undefined } = {
        "de": defaultDesc,
        "en": !_.isEmpty(product.descriptionEn) ? product.descriptionEn : defaultDesc,
        "fr": !_.isEmpty(product.descriptionFr) ? product.descriptionFr : defaultDesc,
        "it": !_.isEmpty(product.descriptionIt) ? product.descriptionIt : defaultDesc
      };

      const lang = currentUser ? currentUser.userConfig.language : "de";
      const desc = texts[lang] ? ` - ${texts[lang]}` : "";

      productText = `${product.id} ${desc}`;
    }
  }

  if (interventionInfo) {
    const bookingRemarks = interventionInfo.bookingRemarks;

    return (
        <ResultsPane onClick={evt => {
          evt.preventDefault();
          evt.stopPropagation()
        }}>
          {/*<div className="results-header">
            {interventionString}
          </div>*/}
          <div className="results-container">

            <div className="intervention-info-property">
              {getMessage("patientCentral.interventionInfo.intervention")}:
            </div>
            <div className="intervention-info-value single-value">
              {externalCode ? getMessage(`enum.InterventionExternalCode.${externalCode}`) : ""}
            </div>


            {/* // INFO VG 2025-02-12: test sample method not needed any more
            <div className="intervention-info-property">
              {getMessage("testSampleMethod")}:
            </div>
            <div className="intervention-info-value single-value">
              {
                interventionInfo.testSampleMethod
                    ? getMessage(`covidTestSampleMethod.${interventionInfo.testSampleMethod}`)
                    : ""
              }
            </div>*/}

            <div className="intervention-info-property">
              {getMessage("remarks")}:
            </div>
            <div className="intervention-info-value single-value">
              {bookingRemarks ? bookingRemarks : getMessage("remarks.noRemarks")}
            </div>

            <div className="intervention-info-property">
              {getMessage("patientCentral.overview.tableHeader.analyzerCode")}:
            </div>
            <div className="intervention-info-value single-value">
              {
                interventionInfo.analyzerCode
                    ? interventionInfo.analyzerCode
                    : ""
              }
            </div>

            {
              productId &&
              <>
                <div className="intervention-info-property">
                  {getMessage("patientCentral.interventionInfo.productId")}:
                </div>
                <div className="intervention-info-value single-value">
                  {productText}
                </div>

                <div className="intervention-info-property">
                  {getMessage("patientCentral.interventionInfo.amountInsurance")}:
                </div>
                <div className="intervention-info-value">
                  {interventionPriceInsurance} CHF
                </div>

                <div className="intervention-info-property second-column">
                  {getMessage("patientCentral.interventionInfo.amountPrivate")}:
                </div>
                <div className="intervention-info-value">
                  {interventionPricePatient} CHF
                </div>

                <div className="intervention-info-property">
                  {getMessage("patientCentral.interventionInfo.inkasso")}:
                </div>
                <div className="intervention-info-value">
                  {
                    interventionInfo.inkasso
                        ? getMessage(`enum.paymentMethod.${interventionInfo.inkasso}`)
                        : ""
                  }
                </div>

                {interventionInfo && interventionInfo.inkasso === PaymentMethod.CREDIT_CARD &&
                <>
                  <div className="intervention-info-property second-column">
                    {getMessage("patientCentral.interventionInfo.creditCardDigits")}:
                  </div>
                  <div className="intervention-info-value">
                    {interventionInfo.creditCardDigits}
                  </div>
                </>
                }

                {interventionInfo && interventionInfo.inkasso === PaymentMethod.TWINT &&
                <>
                  <div className="intervention-info-property second-column">
                    {getMessage("patientCentral.interventionInfo.transactionId")}:
                  </div>
                  <div className="intervention-info-value">
                    {interventionInfo.twintTransactionId}
                  </div>
                </>
                }
              </>
            }
            <div className="intervention-info-property">
              {getMessage("patientCentral.interventionInfo.urgency")}:
            </div>
            <div className="intervention-info-value single-value">
              {
                interventionInfo.urgency
                    ? getMessage(`enum.urgency.${interventionInfo.urgency}`)
                    : ""
              }
            </div>
            {productId && invoiceProduct
                ? <>
                  <div className="intervention-info-property">
                    {getMessage("patientCentral.interventionInfo.generateCertificate")}:
                  </div>
                  <div className="intervention-info-value single-value">
                    <StyledIcon name={invoiceProduct.generateCertificate ? "check" : "x"}/>
                  </div>
                  <div className="intervention-info-property">
                    {getMessage("patientCentral.interventionInfo.generateReport")}:
                  </div>
                  <div className="intervention-info-value single-value">
                    <StyledIcon name={invoiceProduct.generateReport ? "check" : "x"}/>
                  </div>
                </>
                : <>
                  <div className="intervention-info-property">
                    {getMessage("patientCentral.interventionInfo.generateCertificate")}:
                  </div>
                  <div className="intervention-info-value single-value">
                    <StyledIcon name={
                      employeeDefaultParameters && employeeDefaultParameters.generateCertificate
                          ? "check"
                          : "x"
                    }
                    />
                  </div>
                  <div className="intervention-info-property">
                    {getMessage("patientCentral.interventionInfo.generateReport")}:
                  </div>
                  <div className="intervention-info-value single-value">
                    <StyledIcon name={
                      employeeDefaultParameters && employeeDefaultParameters.generateReport
                          ? "check"
                          : "x"
                    }
                    />
                  </div>
                </>
            }
          </div>
        </ResultsPane>
    );
  }

  return <div></div>;
};

export default withMessages(withPatientCentralRefDataContext(InterventionInfoView));
